import React, {useState} from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import {NavLink, Link} from 'react-router-dom'
import {Modal} from 'antd'
import {
  HomeOutlined,
  CloseOutlined,
  PhoneOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
  MenuUnfoldOutlined,
  EnvironmentOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons'
import {Helmet} from 'react-helmet'
import Heading from 'components/shared/Heading'
import Button from 'components/shared/Button'
import Query from 'components/shared/Query'
import Checkbox from 'components/shared/Checkbox'
import logoInviso from 'assets/logo.svg'
import './index.scss'

const sidebarQuery = gql`
  query SidebarQuery {
    viewer {
      id
      customer {
        id
        shouldOrderThroughIntegration
        hasFastoutSubscription
        canCreateOrders
      }
    }
  }
`

const AppLayout = ({
  documentTitle = 'EFKT',
  backToPath,
  children,
  rightAction = null,
}) => {
  const [asideIsOpen, setAsideIsOpen] = useState(false)
  const [confirmNewOrder, setConfirmNewOrder] = useState(false)
  const [hasConfirmedNewOrder, setHasConfirmedNewOrder] = useState(false)
  const toggleAsideIsOpen = () => setAsideIsOpen(!asideIsOpen)

  return (
    <>
      <header id="header">
        <Helmet title={documentTitle} />
        <div>
          {backToPath && (
            <Link to={backToPath} className="left-trigger go-back">
              <Button
                icon={<ArrowLeftOutlined />}
                className="back-to-path"
              />
            </Link>
          )}
          {!backToPath && (
            <Button
              icon={<MenuUnfoldOutlined />}
              className="left-trigger sidebar-trigger"
              onClick={toggleAsideIsOpen}
            />
          )}
          <Heading>{documentTitle}</Heading>
        </div>
        <div>
          {rightAction}
        </div>
      </header>

      {asideIsOpen && (
        <div id="aside-background" onClick={toggleAsideIsOpen} />
      )}

      <aside id="sidebar" className={asideIsOpen ? 'open' : null}>
        <Query query={sidebarQuery}>
          {({data, loading}) => (
            <nav>
              {!loading && data.viewer.customer.canCreateOrders && (
                <NavLink to="/bestilling" onClick={(e) => {
                  if (data.viewer.customer.shouldOrderThroughIntegration) {
                    e.preventDefault()
                    setConfirmNewOrder(true)
                  }
                }}>
                  <ShoppingCartOutlined />
                  Ny bestilling
                </NavLink>
              )}
              <NavLink to="/mine-oppdrag">
                <HomeOutlined />
                Oppdrag
              </NavLink>
              <NavLink to="/innstillinger">
                <SettingOutlined />
                Innstillinger
              </NavLink>
            </nav>
          )}
        </Query>

        <div className="bottom-profile">
          <img src={logoInviso} alt="EFKT" width="84" height="16" />
          <a href="tel:21080500" className="phone">
            <PhoneOutlined />
            <span className="phone-text">21 08 05 00</span>
          </a>
        </div>
      </aside>

      <section id="main-content">
        {children}
      </section>

      <Modal
        title="Bekreft ny bestilling"
        visible={confirmNewOrder}
        onCancel={() => setConfirmNewOrder(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmNewOrder(false)}>
            Avbryt
          </Button>,
          <NavLink key="ok" to="/bestilling">
            <Button type="action" disabled={!hasConfirmedNewOrder}>
              Ny bestilling
            </Button>
          </NavLink>,
        ]}
      >
        <p>Hei</p>
        <p>Vi ser at du er tilknyttet et eget meglersystem. For at leveransen din skal bli levert korrekt inn i ditt system, er det viktig å gjøre bestillingen via dette systemet for å unngå merarbeid i etterkant.</p>
        <p>Du kan gjerne fortsette denne bestillingen her, men da må du laste ned produktene manuelt til ditt system.</p>
        <p>
          <Checkbox
            checked={hasConfirmedNewOrder}
            label="Jeg er inneforstått med at leveransen ikke vil bli lastet opp til eget meglersystem, men ønsker allikevel å opprette ny bestilling"
            onChange={e => setHasConfirmedNewOrder(e.target.checked)}
          />
        </p>
      </Modal>
    </>
  )
}

AppLayout.Drawer = ({heading, children, onClose}) => (
  <aside id="drawer">
    <header>
      <Button
        className="drawer-trigger"
        icon={<CloseOutlined />}
        onClick={onClose}
      />
      {heading}
    </header>

    <div className="drawer-content">
      {children}
    </div>
  </aside>
)

AppLayout.propTypes = {
  documentTitle: PropTypes.string,
  backToPath: PropTypes.string,
  children: PropTypes.node,
}

export default AppLayout
